// Tool action handler - processes actions from Mabel chat
// Extracted from App.js for cleaner separation

/**
 * Process tool actions returned from Mabel AI
 * @param {Array} toolActions - Array of {tool, result} objects
 * @param {Object} handlers - State update handlers from App
 * @param {string} accessToken - Auth token for API calls
 */
export function handleToolActions(toolActions, handlers, accessToken = null) {
  if (!toolActions || toolActions.length === 0) return;

  const {
    // Task handlers
    addFullTask,
    updateTask,
    markDone,
    markUndone,
    toggleStar,
    deleteTask,
    clearAllTasks,
    // Inventory handlers
    addInventoryItem,
    updateInventoryItem,
    removeInventoryItem,
    markAsBought,
    markAsOut,
    // Recipe handlers
    addRecipe,
    updateRecipe,
    deleteRecipe,
    // Filter handlers
    setStatusFilter,
    setCapacity,
    setShowStarredOnly,
    // User state handler
    setUserState,
    // Toast
    showToast
  } = handlers;

  toolActions.forEach(action => {
    const { tool, result } = action;

    if (!result.success) {
      // Show error toast for failed actions (except clarification prompts)
      if (result.action !== 'needs_clarification') {
        showToast(result.message || 'Action failed', 'error');
      }
      return;
    }

    // Task tools
    switch(tool) {
      case 'create_task':
        if (result.task) {
          addFullTask(result.task);
          showToast(`Added: "${result.task.title}"`);
        }
        break;

      case 'update_task':
        if (result.taskId && result.updates) {
          updateTask(result.taskId, result.updates);
          showToast(`Updated: "${result.originalTitle}"`);
        }
        break;

      case 'complete_task':
        if (result.taskId) {
          markDone(result.taskId);
          showToast(`Completed: "${result.taskTitle}"`);
        }
        break;

      case 'uncomplete_task':
        if (result.taskId) {
          markUndone(result.taskId);
          showToast(`Reopened: "${result.taskTitle}"`);
        }
        break;

      case 'toggle_star':
        if (result.taskId) {
          toggleStar(result.taskId);
          showToast(result.message);
        }
        break;

      case 'delete_task':
        if (result.taskId) {
          deleteTask(result.taskId);
          showToast(`Deleted: "${result.taskTitle}"`);
        }
        break;

      case 'clear_all_tasks':
        if (result.action === 'cleared' && clearAllTasks) {
          clearAllTasks();
          showToast(result.message || 'All tasks cleared');
        }
        break;

      // Kitchen tools
      case 'add_pantry_item':
        if (result.item && result.action !== 'already_exists') {
          addInventoryItem(result.item);
          showToast(`Added ${result.item.name} to pantry`);
        } else if (result.action === 'already_exists') {
          showToast(`${result.item.name} already in pantry`);
        }
        break;

      case 'add_to_shopping_list':
        if (result.item && result.action !== 'already_on_list') {
          addInventoryItem(result.item);
          showToast(`Added ${result.item.name} to shopping list`);
        } else if (result.action === 'already_on_list') {
          showToast(`${result.item.name} already on list`);
        }
        break;

      case 'mark_item_bought':
        if (result.itemId) {
          markAsBought(result.itemId);
          showToast(`Got ${result.item.name}!`);
        }
        break;

      case 'mark_item_out':
        if (result.itemId) {
          markAsOut(result.itemId);
          if (result.addToShopping) {
            // Add to shopping list
            addInventoryItem({
              name: result.item.name,
              domain: 'kitchen',
              category: result.item.category,
              status: 'need_to_buy'
            });
          }
          showToast(`Marked ${result.item.name} as out`);
        }
        break;

      case 'remove_pantry_item':
        if (result.itemId) {
          removeInventoryItem(result.itemId);
          showToast(`Removed ${result.item.name}`);
        }
        break;

      case 'update_pantry_item':
        if (result.itemId && result.updates) {
          updateInventoryItem(result.itemId, result.updates);
          showToast(`Updated ${result.item.name}`);
        }
        break;

      case 'remove_from_shopping_list':
        if (result.itemId) {
          removeInventoryItem(result.itemId);
          showToast(`Removed ${result.item.name} from shopping list`);
        }
        break;

      // Read-only tools (no state change needed)
      case 'check_pantry':
      case 'check_shopping_list':
        // These just return info, no action needed
        break;

      // Filter tools
      case 'set_status_filter':
      case 'show_done_tasks':
      case 'hide_done_tasks':
        if (result.filterChanges?.statusFilter) {
          setStatusFilter(result.filterChanges.statusFilter);
          showToast(result.message);
        }
        break;

      case 'set_priority_filter':
      case 'set_area_filter':
        if (result.filterChanges?.capacity) {
          setCapacity(prev => ({
            ...prev,
            ...result.filterChanges.capacity
          }));
          showToast(result.message);
        }
        break;

      case 'clear_all_filters':
        if (result.filterChanges) {
          setStatusFilter(result.filterChanges.statusFilter);
          setShowStarredOnly(result.filterChanges.showStarredOnly);
          setCapacity(result.filterChanges.capacity);
          showToast(result.message);
        }
        break;

      // User state tools
      case 'update_user_state':
        if (result.stateUpdates) {
          setUserState(prev => {
            const newState = {
              ...prev,
              ...result.stateUpdates,
              timestamp: new Date().toISOString()
            };
            // Persist to localStorage
            localStorage.setItem('mabel-user-state', JSON.stringify(newState));
            return newState;
          });
          showToast(result.message || 'State updated');
        }
        break;

      // Meal plan tools - use state handlers for immediate UI sync
      case 'add_meal_to_plan':
        if (result.meal && handlers.addMeal) {
          handlers.addMeal(result.meal);
          showToast(result.message);
        }
        break;

      case 'skip_meal_day':
        if (result.date && handlers.skipMealDay) {
          handlers.skipMealDay(result.date);
          showToast(result.message);
        }
        break;

      case 'remove_meal_from_plan':
        if (result.date && handlers.removeMeal) {
          handlers.removeMeal(result.date);
          showToast(result.message);
        }
        break;

      case 'check_meal_plan':
        // Read-only, no state change needed
        break;

      // Recipe tools
      case 'add_recipe':
        if (result.recipe && addRecipe) {
          addRecipe(result.recipe);
          showToast(result.message || `Saved recipe: ${result.recipe.name}`);
        }
        break;

      case 'update_recipe':
        if (result.recipeId && result.updates && updateRecipe) {
          updateRecipe(result.recipeId, result.updates);
          showToast(result.message || 'Recipe updated');
        }
        break;

      case 'delete_recipe':
        if (result.recipeId && deleteRecipe) {
          deleteRecipe(result.recipeId);
          showToast(result.message || 'Recipe deleted');
        }
        break;

      case 'search_recipes':
      case 'list_recipes':
      case 'get_recipe':
      case 'suggest_from_pantry':
        // Read-only, no state change needed
        break;

      default:
        console.warn('Unknown tool action:', tool);
    }
  });
}
